import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaChevronDown } from "react-icons/fa";
import "../assets/css/AddLead.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import { getAuthItem } from "../utils/authStorage";
import { formatPascal } from "../utils/formatters";
import { formValueWithMobileSanitize, mobileValidationMessage } from "../utils/phone";

// Qualification order: highest academic level to lowest (for dropdown)
const QUALIFICATION_ORDER = [
  "PhD", "M.Tech / ME", "MBA", "MCA", "M.Sc", "MA",
  "B.Tech / BE", "B.Sc", "BA", "BBA", "BCA",
  "Diploma", "Intermediate / 12th", "High School", "Other"
];
const sortQualificationsByLevel = (list) => {
  if (!Array.isArray(list)) return list;
  return [...list].sort((a, b) => {
    const nameA = (a.Qualification_Name || a.qualification_name || "").trim();
    const nameB = (b.Qualification_Name || b.qualification_name || "").trim();
    const idxA = QUALIFICATION_ORDER.findIndex((n) => n === nameA || n.toLowerCase() === nameA.toLowerCase());
    const idxB = QUALIFICATION_ORDER.findIndex((n) => n === nameB || n.toLowerCase() === nameB.toLowerCase());
    const orderA = idxA === -1 ? 999 : idxA;
    const orderB = idxB === -1 ? 999 : idxB;
    return orderA - orderB;
  });
};

function AddTrainingLead({ onBack, onAddLead, courses = [] }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    personalPhone: "",
    altPhone: "",
    email: "",
    qualification: "",
    customQualification: "",
    collegeName: "",
    yearOfPassing: "",
    expectedFee: "",
    earliestStartDate: "",
    batch: "",
    modeIds: [],
    courseId: "",
    source: "",
    status: "",
    assignedTo: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: ""
  });

  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [loadingQualifications, setLoadingQualifications] = useState(false);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [states, setStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addressSearch, setAddressSearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState(false);
  const addressSearchTimeoutRef = useRef(null);
  const addressSearchRef = useRef(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [modes, setModes] = useState([]);
  const [loadingModes, setLoadingModes] = useState(false);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState("");
  const courseDropdownRef = useRef(null);
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [sourceSearchQuery, setSourceSearchQuery] = useState("");
  const sourceDropdownRef = useRef(null);
  const [qualificationDropdownOpen, setQualificationDropdownOpen] = useState(false);
  const [qualificationSearchQuery, setQualificationSearchQuery] = useState("");
  const qualificationDropdownRef = useRef(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSearchQuery, setStatusSearchQuery] = useState("");
  const statusDropdownRef = useRef(null);

  // Fetch modes from API
  useEffect(() => {
    const fetchModes = async () => {
      setLoadingModes(true);
      try {
        const response = await axiosInstance.get("/modes");
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((m) => ({
            id: m.Mode_Id ?? m.mode_id ?? m.id,
            name: m.Mode_Name ?? m.mode_name ?? m.name ?? ""
          }))
          .filter((m) => m.id != null && String(m.name || "").trim() !== "");
        setModes(mapped);
      } catch (error) {
        console.error("Error fetching modes:", error);
        setModes([]);
      } finally {
        setLoadingModes(false);
      }
    };
    fetchModes();
  }, []);


  // Fetch active DigiX staff users for Assigned To
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const response = await axiosInstance.get("/users", { params: { status: "Active" } });
        const list = Array.isArray(response.data) ? response.data : [];
        const mapped = list
          .map((u) => ({
            id: u.user_id ?? u.User_Id ?? u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || `User #${u.user_id}`,
          }))
          .filter((u) => u.id != null)
          .sort((a, b) => a.name.localeCompare(b.name));
        setUsers(mapped);
        let currentId = "";
        try {
          const userStr = getAuthItem("user");
          if (userStr) {
            const user = JSON.parse(userStr);
            currentId = String(user.user_id ?? user.id ?? "");
          }
        } catch {
          /* ignore */
        }
        setFormData((prev) => {
          if (prev.assignedTo) return prev;
          const fallback = currentId || (mapped[0] ? String(mapped[0].id) : "");
          return { ...prev, assignedTo: fallback };
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        setUsers([]);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch lead sources from API
  useEffect(() => {
    const fetchSources = async () => {
      setLoadingSources(true);
      try {
        const response = await axiosInstance.get('/lead-sources');
        if (response.data && Array.isArray(response.data)) {
          setSources(response.data);
        }
      } catch (error) {
        console.error("Error fetching sources:", error);
      } finally {
        setLoadingSources(false);
      }
    };

    fetchSources();
  }, []);

  // Fetch training lead statuses from API (same as filter section in DigixLeads)
  useEffect(() => {
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get('/lead-statuses');
        const raw = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];
        const mapped = raw
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? ""
          }))
          .filter((s) => s.id != null && s.name.trim() !== "");
        setLeadStatuses(mapped);
        const newStatus = mapped.find((s) => s.name.toLowerCase() === "new");
        if (newStatus) setFormData((p) => ({ ...p, status: String(newStatus.id) }));
      } catch (error) {
        console.error("Error fetching lead statuses for training lead:", error);
        setLeadStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchStatuses();
  }, []);

  // Fetch qualifications from API
  useEffect(() => {
    const fetchQualifications = async () => {
      setLoadingQualifications(true);
      try {
        const response = await axiosInstance.get('/qualifications');
        if (response.data && Array.isArray(response.data)) {
          setQualifications(sortQualificationsByLevel(response.data));
        }
      } catch (error) {
        console.error("Error fetching qualifications:", error);
      } finally {
        setLoadingQualifications(false);
      }
    };

    fetchQualifications();
  }, []);

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await axiosInstance.get('/countries');
        if (response.data && Array.isArray(response.data)) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries();
  }, []);

  // Close course dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (courseDropdownRef.current && !courseDropdownRef.current.contains(e.target)) {
        setIsCourseDropdownOpen(false);
        setCourseSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target)) { setSourceDropdownOpen(false); setSourceSearchQuery(""); }
      if (qualificationDropdownRef.current && !qualificationDropdownRef.current.contains(e.target)) { setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) { setStatusDropdownOpen(false); setStatusSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch states when country is selected
  useEffect(() => {
    const fetchStates = async () => {
      if (!selectedCountryId) {
        setStates([]);
        setFormData(prev => ({ ...prev, state: "" }));
        return;
      }

      setLoadingStates(true);
      try {
        const response = await axiosInstance.get(`/states/${selectedCountryId}`);
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data);
        }
      } catch (error) {
        console.error("Error fetching states:", error);
        try {
          const altResponse = await axiosInstance.get(`/api/states/${selectedCountryId}`);
          if (altResponse.data && Array.isArray(altResponse.data)) {
            setStates(altResponse.data);
          }
        } catch (altError) {
          console.error("Error with alternative endpoint:", altError);
          setStates([]);
        }
      } finally {
        setLoadingStates(false);
      }
    };

    fetchStates();
  }, [selectedCountryId]);

  // Fetch cities when state is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (!selectedStateId) {
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
        return;
      }

      setLoadingCities(true);
      try {
        const response = await axiosInstance.get(`/cities/${selectedStateId}`);
        if (response.data && Array.isArray(response.data)) {
          setCities(response.data);
        }
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [selectedStateId]);

  // Fetch address suggestions from Google Maps API
  const fetchAddressSuggestions = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoadingAddressSuggestions(true);
    try {
      const response = await axiosInstance.get(`/get-address?address=${encodeURIComponent(searchTerm.trim())}`);
      if (response.data && response.data.suggestions && Array.isArray(response.data.suggestions)) {
        setAddressSuggestions(response.data.suggestions);
        setShowSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setAddressSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingAddressSuggestions(false);
    }
  };

  // Handle address search input with debouncing
  const handleAddressSearchChange = (e) => {
    const value = e.target.value;
    setAddressSearch(value);

    // Clear previous timeout
    if (addressSearchTimeoutRef.current) {
      clearTimeout(addressSearchTimeoutRef.current);
    }

    // Set new timeout for debouncing (500ms delay)
    addressSearchTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 500);
  };

  // Fetch address details when a suggestion is selected
  const handleAddressSuggestionSelect = async (suggestion) => {
    setAddressSearch(suggestion.description);
    setShowSuggestions(false);
    setAddressSuggestions([]);

    try {
      const response = await axiosInstance.get(`/get-address-details?placeId=${encodeURIComponent(suggestion.place_id)}`);
      if (response.data) {
        const details = response.data;
        
        // Update form data with address details
        setFormData(prev => ({
          ...prev,
          addressLine1: details.addressLine1 || prev.addressLine1,
          addressLine2: details.addressLine2 || prev.addressLine2
        }));

        // Find and set country
        if (details.country) {
          const countryMatch = countries.find(c => 
            c.country_name?.toLowerCase() === details.country.toLowerCase()
          );
          if (countryMatch) {
            setSelectedCountryId(countryMatch.country_id);
            setFormData(prev => ({ ...prev, country: countryMatch.country_name }));
            
            // Fetch states for the selected country
            try {
              const statesResponse = await axiosInstance.get(`/states/${countryMatch.country_id}`);
              if (statesResponse.data && Array.isArray(statesResponse.data)) {
                setStates(statesResponse.data);
                
                // Find and set state
                if (details.state) {
                  const stateMatch = statesResponse.data.find(s => 
                    s.state_name?.toLowerCase() === details.state.toLowerCase()
                  );
                  if (stateMatch) {
                    setSelectedStateId(stateMatch.state_id);
                    setFormData(prev => ({ ...prev, state: stateMatch.state_name }));
                    
                    // Fetch cities for the selected state
                    try {
                      const citiesResponse = await axiosInstance.get(`/cities/${stateMatch.state_id}`);
                      if (citiesResponse.data && Array.isArray(citiesResponse.data)) {
                        setCities(citiesResponse.data);
                        
                        // Find and set city
                        if (details.city) {
                          const cityMatch = citiesResponse.data.find(c => 
                            c.city_name?.toLowerCase() === details.city.toLowerCase()
                          );
                          if (cityMatch) {
                            setFormData(prev => ({ ...prev, city: cityMatch.city_name }));
                          } else {
                            // If city not found in list, set it directly
                            setFormData(prev => ({ ...prev, city: details.city }));
                          }
                        }
                      } else {
                        // If cities fetch fails, still set city name
                        if (details.city) {
                          setFormData(prev => ({ ...prev, city: details.city }));
                        }
                      }
                    } catch (cityError) {
                      console.error("Error fetching cities:", cityError);
                      // Still set city name even if fetch fails
                      if (details.city) {
                        setFormData(prev => ({ ...prev, city: details.city }));
                      }
                    }
                  } else {
                    // If state not found, set it directly
                    setFormData(prev => ({ ...prev, state: details.state }));
                  }
                }
              }
            } catch (stateError) {
              console.error("Error fetching states:", stateError);
              // Still set state name even if fetch fails
              if (details.state) {
                setFormData(prev => ({ ...prev, state: details.state }));
              }
            }
          } else {
            // If country not found, set it directly
            setFormData(prev => ({ ...prev, country: details.country }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching address details:", error);
      alert("Failed to fetch address details. Please try again.");
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addressSearchRef.current && !addressSearchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addressSearchTimeoutRef.current) {
        clearTimeout(addressSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear customQualification when switching away from "Other"
    if (name === "qualification" && value !== "15") {
      setFormData(prev => ({ ...prev, [name]: value, customQualification: "" }));
      return;
    }
    
    // Handle country selection - find country_id from country_name
    if (name === "country") {
      const selectedCountry = countries.find(c => c.country_name === value);
      if (selectedCountry) {
        setSelectedCountryId(selectedCountry.country_id);
      } else {
        setSelectedCountryId(null);
        // Reset state and city when country is cleared
        setSelectedStateId(null);
        setStates([]);
        setCities([]);
        setFormData(prev => ({ ...prev, state: "", city: "" }));
      }
    }
    
    // Handle state selection - find state_id from state_name
    if (name === "state") {
      const selectedState = states.find(s => s.state_name === value);
      if (selectedState) {
        setSelectedStateId(selectedState.state_id);
      } else {
        setSelectedStateId(null);
        // Reset city when state is cleared
        setCities([]);
        setFormData(prev => ({ ...prev, city: "" }));
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formValueWithMobileSanitize(name, value)
    }));
  };

  const handleToggleCourse = (courseId) => {
    setSelectedCourseIds((prev) => {
      if (prev.includes(courseId)) {
        return prev.filter((id) => id !== courseId);
      }
      return [...prev, courseId];
    });
  };

  const handleToggleAllCourses = () => {
    const activeCourses = (courses || []).filter((c) => c.Status === "Active");
    if (selectedCourseIds.length === activeCourses.length) {
      setSelectedCourseIds([]);
    } else {
      setSelectedCourseIds(activeCourses.map((c) => c.Course_Id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName) {
      alert("Please fill in required fields (First Name and Phone).");
      return;
    }
    const phoneError = mobileValidationMessage(formData.personalPhone, { required: true, label: "Phone" });
    if (phoneError) {
      alert(phoneError);
      return;
    }
    const altError = mobileValidationMessage(formData.altPhone, { label: "Alternate phone" });
    if (altError) {
      alert(altError);
      return;
    }
    if (!formData.source) {
      alert("Please select a source.");
      return;
    }
    if (!selectedCourseIds || selectedCourseIds.length === 0) {
      alert("Please select at least one course.");
      return;
    }

    // Get logged-in user id from localStorage
    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (error) {
      console.error("Error reading user data:", error);
    }

    // Find city_id, state_id, country_id from names
    const selectedCountry = countries.find(c => c.country_name === formData.country);
    const selectedState = states.find(s => s.state_name === formData.state);
    const selectedCity = cities.find(c => c.city_name === formData.city);

    const payload = {
      First_Name: formatPascal(formData.firstName),
      Last_Name: formatPascal(formData.lastName),
      Phone: formData.personalPhone || null,
      Alt_Phone: formData.altPhone || null,
      Email: formData.email || null,
      Qualification_Id: formData.qualification ? parseInt(formData.qualification, 10) : null,
      ...(String(formData.qualification) === "15" && formData.customQualification
        ? { Other_Qualification: formData.customQualification }
        : {}),
      College_Name: formData.collegeName ? formatPascal(formData.collegeName) : null,
      Year_Of_Passing: formData.yearOfPassing || null,
      Expected_Fee: formData.expectedFee ? parseFloat(formData.expectedFee) : null,
      Earliest_Start_Date: formData.earliestStartDate || null,
      Batch: formData.batch || null,
      Course_Ids: selectedCourseIds.length ? selectedCourseIds.map((id) => parseInt(id, 10)) : null,
      Mode_Ids: Array.isArray(formData.modeIds) && formData.modeIds.length > 0 ? formData.modeIds : null,
      Source_Id: formData.source ? parseInt(formData.source) : null,
      Status_Id: formData.status ? parseInt(formData.status) : null,
      Assigned_To: formData.assignedTo ? parseInt(formData.assignedTo, 10) : createdBy,
      Country_Code: phoneCountryCode || null,
      Address_Line_1: formData.addressLine1 || null,
      Address_Line_2: formData.addressLine2 || null,
      City_Id: selectedCity ? selectedCity.city_id : null,
      State_Id: selectedState ? selectedState.state_id : null,
      Country_Id: selectedCountry ? selectedCountry.country_id : null
    };

    setSubmitting(true);
    try {
      const response = await axiosInstance.post('/create-training-lead', payload);
      
      const successMessage = response.data.message || "Training lead created successfully";
      const dataKey = "training_lead";
      
      if (response.data && response.data[dataKey]) {
        alert(successMessage);

        // Call parent callback with the created data
        if (onAddLead) {
          onAddLead(response.data[dataKey]);
        }

        // Navigate back
        if (onBack) {
          onBack();
        }
      }
    } catch (error) {
      console.error("Error creating training lead:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to create training lead. Please try again.";
      alert(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="addlead-container">
      <button type="button" className="back-link" onClick={onBack}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="addlead-form" onSubmit={handleSubmit}>
        {/* Contact Details */}
        <div className="form-section">
          <h2 className="section-title">Contact Details</h2>
          <div className="form-grid addtraininglead-form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Phone <span className="required">*</span></label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  style={{ width: "80px", flexShrink: 0 }}
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                >
                  <option value="">Code</option>
                  {countries.map((country) => {
                    const code = (country.country_code || country.country_short_code || "").trim();
                    return (
                      <option key={country.country_id} value={code}>
                        {code}
                      </option>
                    );
                  })}
                </select>
                <input
                  style={{ flex: 1 }}
                  type="tel"
                  name="personalPhone"
                  value={formData.personalPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Alt Phone</label>
              <input
                type="tel"
                name="altPhone"
                value={formData.altPhone}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Source <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={sourceDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingSources ? 0.6 : 1, cursor: loadingSources ? "default" : "pointer" }} onClick={() => !loadingSources && setSourceDropdownOpen((o) => !o)} role="button" tabIndex={loadingSources ? -1 : 0} onKeyDown={(e) => { if (!loadingSources && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSourceDropdownOpen((o) => !o); } }}>
                  <span>{loadingSources ? "Loading sources..." : (formData.source ? (sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.source_name || sources.find((s) => String(s.source_id ?? s.Source_Id ?? s.id) === String(formData.source))?.Source_Name || "Select Source") : "Select Source")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${sourceDropdownOpen ? "open" : ""}`} />
                </div>
                {sourceDropdownOpen && !loadingSources && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search sources..." value={sourceSearchQuery} onChange={(e) => setSourceSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.source ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: "" })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>Select Source</div>
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).map((s) => {
                        const sid = String(s.source_id ?? s.Source_Id ?? s.id);
                        const sname = s.source_name || s.Source_Name || "";
                        return (
                          <div key={sid} className={`domain-filter-option ${String(formData.source) === sid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, source: sid })); setSourceDropdownOpen(false); setSourceSearchQuery(""); }}>{sname}</div>
                        );
                      })}
                      {sources.filter((s) => !sourceSearchQuery.trim() || String(s.source_name || s.Source_Name || "").toLowerCase().includes(sourceSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No sources found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Status</label>
              <div className="domain-filter-dropdown" ref={statusDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingStatuses ? 0.6 : 1, cursor: loadingStatuses ? "default" : "pointer" }} onClick={() => !loadingStatuses && setStatusDropdownOpen((o) => !o)} role="button" tabIndex={loadingStatuses ? -1 : 0} onKeyDown={(e) => { if (!loadingStatuses && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setStatusDropdownOpen((o) => !o); } }}>
                  <span>{loadingStatuses ? "Loading statuses..." : (formData.status ? (leadStatuses.find((s) => String(s.id) === String(formData.status))?.name || "Select Status") : "Select Status")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${statusDropdownOpen ? "open" : ""}`} />
                </div>
                {statusDropdownOpen && !loadingStatuses && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search statuses..." value={statusSearchQuery} onChange={(e) => setStatusSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.status ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: "" })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>Select Status</div>
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).map((s) => (
                        <div key={s.id} className={`domain-filter-option ${String(formData.status) === String(s.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, status: String(s.id) })); setStatusDropdownOpen(false); setStatusSearchQuery(""); }}>{s.name}</div>
                      ))}
                      {leadStatuses.filter((s) => !statusSearchQuery.trim() || String(s.name || "").toLowerCase().includes(statusSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No statuses found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Assigned To</label>
              <select
                name="assignedTo"
                value={formData.assignedTo}
                onChange={handleInputChange}
                disabled={loadingUsers}
              >
                {loadingUsers && <option value="">Loading users...</option>}
                {!loadingUsers && <option value="">Select user</option>}
                {users.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Training Details */}
        <div className="form-section">
          <h2 className="section-title">Training Details</h2>
          <div className="form-grid addtraininglead-form-grid">
            <div className="form-group" ref={courseDropdownRef}>
              <label>Courses <span className="required">*</span></label>
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setIsCourseDropdownOpen((open) => !open);
                  }
                }}
                className="permissions-select-trigger"
                onClick={() => setIsCourseDropdownOpen((open) => !open)}
              >
                <span className="permissions-select-text">
                  {(!courses || courses.length === 0) ? "No courses available"
                    : selectedCourseIds.length === 0
                      ? "Select Courses"
                      : `${selectedCourseIds.length} course(s) selected`}
                </span>
                <FaChevronDown
                  className={`permissions-select-chevron ${isCourseDropdownOpen ? "open" : ""}`}
                />
              </div>
              {isCourseDropdownOpen && courses && courses.length > 0 && (
                <div className="permissions-select-panel" style={{ maxHeight: 220, overflowY: "auto" }}>
                  <input type="text" className="domain-filter-search" placeholder="Search courses..." value={courseSearchQuery} onChange={(e) => setCourseSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ margin: "8px", width: "calc(100% - 16px)", boxSizing: "border-box" }} />
                  <label
                    className="permissions-select-item"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={
                        selectedCourseIds.length ===
                        courses.filter((c) => c.Status === "Active").length
                      }
                      onChange={handleToggleAllCourses}
                    />
                    <span className="permissions-select-item-label">Select all</span>
                  </label>
                  {courses
                    .filter((course) => course.Status === "Active")
                    .filter((course) => !courseSearchQuery.trim() || String((course.Course_Code || "") + " " + (course.Course_Name || "")).toLowerCase().includes(courseSearchQuery.trim().toLowerCase()))
                    .map((course) => (
                      <label
                        key={course.Course_Id}
                        className="permissions-select-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedCourseIds.includes(course.Course_Id)}
                          onChange={() => handleToggleCourse(course.Course_Id)}
                        />
                        <span className="permissions-select-item-label">
                          {course.Course_Code} - {course.Course_Name}
                        </span>
                      </label>
                    ))}
                  {courses.filter((course) => course.Status === "Active").filter((course) => !courseSearchQuery.trim() || String((course.Course_Code || "") + " " + (course.Course_Name || "")).toLowerCase().includes(courseSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results" style={{ padding: "12px" }}>No courses found</div>}
                </div>
              )}
              {selectedCourseIds.length > 0 && courses && (
                <div style={{ marginTop: 8 }}>
                  {selectedCourseIds.map((id) => {
                    const course = courses.find((c) => c.Course_Id === id);
                    if (!course || !course.Description) return null;
                    return (
                      <p
                        key={id}
                        className="course-description"
                        style={{ marginTop: 4, fontSize: "13px", color: "#7A8CA5" }}
                      >
                        {course.Course_Code} - {course.Course_Name}: {course.Description}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Mode</label>
              {loadingModes ? (
                <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>Loading modes...</p>
              ) : modes.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 16px", alignItems: "center" }}>
                  {modes.map((mode) => (
                    <label key={mode.id} style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={(formData.modeIds || []).includes(mode.id)}
                        onChange={(e) => {
                          const prevIds = Array.isArray(formData.modeIds) ? [...formData.modeIds] : [];
                          const nextIds = e.target.checked
                            ? (prevIds.includes(mode.id) ? prevIds : [...prevIds, mode.id])
                            : prevIds.filter((id) => id !== mode.id);
                          setFormData((prev) => ({ ...prev, modeIds: nextIds }));
                        }}
                        style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#2977ed" }}
                      />
                      <span>{mode.name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: "13px", color: "#999" }}>No modes available</p>
              )}
            </div>
            <div className="form-group">
              <label>Qualification</label>
              <div className="domain-filter-dropdown" ref={qualificationDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingQualifications ? 0.6 : 1, cursor: loadingQualifications ? "default" : "pointer" }} onClick={() => !loadingQualifications && setQualificationDropdownOpen((o) => !o)} role="button" tabIndex={loadingQualifications ? -1 : 0} onKeyDown={(e) => { if (!loadingQualifications && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setQualificationDropdownOpen((o) => !o); } }}>
                  <span>{loadingQualifications ? "Loading qualifications..." : (formData.qualification ? ((() => { const q = qualifications.find((q) => String(q.Qualification_Id ?? q.qualification_id ?? q.id) === String(formData.qualification)); return q?.Qualification_Name || q?.qualification_name || "Select Qualification"; })()) : "Select Qualification")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${qualificationDropdownOpen ? "open" : ""}`} />
                </div>
                {qualificationDropdownOpen && !loadingQualifications && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search qualifications..." value={qualificationSearchQuery} onChange={(e) => setQualificationSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.qualification ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, qualification: "", customQualification: "" })); setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }}>Select Qualification</div>
                      {qualifications.filter((q) => !qualificationSearchQuery.trim() || String(q.Qualification_Name || q.qualification_name || "").toLowerCase().includes(qualificationSearchQuery.trim().toLowerCase())).map((q) => {
                        const qid = String(q.Qualification_Id ?? q.qualification_id ?? q.id);
                        const qname = q.Qualification_Name || q.qualification_name || "";
                        return (
                          <div key={qid} className={`domain-filter-option ${String(formData.qualification) === qid ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, qualification: qid, customQualification: qid === "15" ? p.customQualification : "" })); setQualificationDropdownOpen(false); setQualificationSearchQuery(""); }}>{qname}</div>
                        );
                      })}
                      {qualifications.filter((q) => !qualificationSearchQuery.trim() || String(q.Qualification_Name || q.qualification_name || "").toLowerCase().includes(qualificationSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No qualifications found</div>}
                    </div>
                  </div>
                )}
              </div>
              {formData.qualification === "15" && (
                <input
                  type="text"
                  name="customQualification"
                  value={formData.customQualification}
                  onChange={handleInputChange}
                 
                  style={{ marginTop: "8px", width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ddd" }}
                />
              )}
            </div>
            <div className="form-group">
              <label>College Name</label>
              <input
                type="text"
                name="collegeName"
                value={formData.collegeName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Year of Passing</label>
              <input
                type="number"
                name="yearOfPassing"
                value={formData.yearOfPassing}
                onChange={handleInputChange}
                min="1900"
                max="2100"
              />
            </div>
            <div className="form-group">
              <label>Expected Fee</label>
              <input
                type="number"
                name="expectedFee"
                value={formData.expectedFee}
                onChange={handleInputChange}
                min="0"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Earliest Start Date</label>
              <input
                type="date"
                name="earliestStartDate"
                value={formData.earliestStartDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Batch</label>
              <select
                name="batch"
                value={formData.batch || ""}
                onChange={handleInputChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "4px", border: "1px solid #ddd", fontSize: "14px" }}
              >
                <option value="">Select</option>
                <option value="Morning">Morning</option>
                <option value="Afternoon">Afternoon</option>
                <option value="Evening">Evening</option>
              </select>
            </div>
                                  </div>
        </div>

        {/* Address */}
        <div className="form-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", paddingBottom: "10px", borderBottom: "1px solid #E5EDF5" }}>
            <h2 className="section-title" style={{ margin: 0, paddingBottom: 0, borderBottom: "none" }}>Address</h2>
            <div style={{ position: "relative", width: "300px", marginBottom: 0 }} ref={addressSearchRef}>
              <input
                type="text"
                className="filter-input"
                placeholder="Search Address..."
                value={addressSearch}
                onChange={handleAddressSearchChange}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
              />
              {loadingAddressSuggestions && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: "4px", padding: "8px", zIndex: 1000 }}>
                  Searching...
                </div>
              )}
              {showSuggestions && addressSuggestions.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1px solid #ddd", borderRadius: "4px", maxHeight: "200px", overflowY: "auto", zIndex: 1000, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleAddressSuggestionSelect(suggestion)}
                      style={{ padding: "10px", cursor: "pointer", borderBottom: index < addressSuggestions.length - 1 ? "1px solid #eee" : "none" }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                    >
                      {suggestion.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={loadingCountries}
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.country_id} value={country.country_name}>
                    {country.country_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>State</label>
              <select
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                disabled={!selectedCountryId || loadingStates}
              >
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state.state_id} value={state.state_name}>
                    {state.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <select
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                disabled={!selectedStateId || loadingCities}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.city_id} value={city.city_name}>
                    {city.city_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button type="submit" className="btn-add-lead" disabled={submitting} style={{ maxWidth: '200px' }}>
            {submitting ? "Adding..." : "Add Training Lead"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddTrainingLead;
